"use client";

import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { CheckCircleIcon, CircleAlertIcon, XCircleIcon } from "lucide-react";

interface AlertProps {
  type?: "success" | "error" | "info";
  message: string;
  duration?: number;
  onClose?: () => void;
  testId?: string;
}

const Alert: React.FC<AlertProps> = ({
  type = "info",
  message,
  duration = 3000,
  onClose,
  testId,
}) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [visible, duration, onClose]);

  if (!visible) return null;

  const baseStyles =
    "flex items-center justify-between rounded-lg px-4 py-3 text-sm shadow-md transition-all max-w-sm w-full";

  const styles = {
    success: "bg-green-100 text-green-800 border border-green-300",
    error: "bg-red-100 text-red-800 border border-red-300",
    info: "bg-blue-100 text-blue-800 border border-blue-300",
  };

  const icons = {
    success: <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />,
    error: <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />,
    info: <CircleAlertIcon className="h-5 w-5 text-blue-500 mr-2" />,
  };

  const contentStyles = {
    success: "bg-green-500 text-white",
    error: "bg-red-500 text-white",
    info: "bg-blue-500 text-white",
  };

  return (
    <div
      className={clsx(baseStyles, styles[type])}
      role="alert"
      aria-live={type === "error" ? "assertive" : "polite"}
      data-testid={testId}
      data-alert-type={type}
    >
      <div
        className={clsx(
          "flex items-center p-3 rounded-md shadow-md transition-all duration-500 transform",
          contentStyles[type]
        )}
      >
        {icons[type]}
        <span>{message}</span>
      </div>
      <button
        type="button"
        onClick={() => {
          setVisible(false);
          onClose?.();
        }}
        className="ml-3 text-gray-600 hover:text-gray-800"
        aria-label="Close alert"
      >
        x
      </button>
    </div>
  );
};

export default Alert;
